import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { KeyRoundIcon, LogOutIcon, Menu, Monitor, PersonStanding } from "lucide-react";
import DesktopNavbar from "./DesktopNavbar";
import Avatar from "@/components/Avatar";
import { useAuth } from "@/hooks/auth";
import { useGlobalMenuSheet, usePlayerlistSheet, useServerSheet } from "@/hooks/sheets";
import { useAtomValue } from "jotai";
import { serverNameAtom } from "@/hooks/status";
import { playerCountAtom } from "@/hooks/playerlist";
import { useAccountModal } from "@/hooks/dialogs";
import { Wordmark } from "@/components/Logos";
import { NavLink } from "@/components/MainPageLink";
import { HoverTip } from '@/components/ui/tooltip';


function ServerTitle() {
    const playerCount = useAtomValue(playerCountAtom);
    const serverName = useAtomValue(serverNameAtom);

    return (
        <div className="flex min-w-0 items-center justify-start gap-1.5">
            <h1 className="truncate-flex font-highlight text-caption uppercase tracking-[.14em] text-fg scrim">
                {serverName}
            </h1>
            <HoverTip label="players connected">
                <span className="shrink-0 font-highlight text-caption tabular text-accent scrim-accent">
                    :&nbsp;{playerCount}
                </span>
            </HoverTip>
        </div>
    );
}


type NavButtonProps = {
    className?: string;
};
const navButtonClasses = `pressable h-11 w-11 sm:h-10 lg:h-9 sm:w-auto sm:min-w-max sm:px-2.5 lg:px-3
    flex justify-center items-center gap-2 cursor-pointer
    rounded-tile border border-edge bg-tile text-dim outline-none
    font-rad text-caption font-semibold uppercase tracking-[.1em] scrim
    hover:border-edge-strong hover:text-fg
`;

function ButtonToggleServerSheet({ className }: NavButtonProps) {
    const { setIsSheetOpen } = useServerSheet();
    return (
        <HoverTip label="Server Menu">
            <button
                className={cn(navButtonClasses, className)}
                aria-label="Server Menu"
                onClick={() => setIsSheetOpen(true)}
            >
                <Monitor className="h-6 w-6 sm:h-5 sm:w-5" />
                <div className="hidden sm:flex flex-row min-w-max align-middle">
                    Server
                </div>
            </button>
        </HoverTip>
    );
}

function ButtonToggleGlobalMenu({ className }: NavButtonProps) {
    const { setIsSheetOpen } = useGlobalMenuSheet();
    return (
        <HoverTip label="Global Menu">
            <button
                className={cn(navButtonClasses, className)}
                aria-label="Global Menu"
                onClick={() => setIsSheetOpen(true)}
            >
                <Menu className="h-6 w-6 sm:h-5 sm:w-5" />
                <div className="hidden sm:flex flex-row min-w-max">
                    Menu
                </div>
            </button>
        </HoverTip>
    );
}

function ButtonTogglePlayerlistSheet({ className }: NavButtonProps) {
    const { setIsSheetOpen } = usePlayerlistSheet();
    const playerCount = useAtomValue(playerCountAtom);

    return (
        <HoverTip label="Global Menu">
            <button
                className={cn(navButtonClasses, className)}
                aria-label="Global Menu"
                onClick={() => setIsSheetOpen(true)}
            >
                <PersonStanding className="h-6 w-6 sm:h-5 sm:w-5" />
                <div className="hidden sm:flex flex-row min-w-max">
                    Players
                    <span className="hidden lg:inline-block font-mono">: {playerCount}</span>
                </div>
            </button>
        </HoverTip>
    );
}

//Segmenting this into a component prevents full header rerenders
function AuthedHeaderFragment() {
    const { authData, logout } = useAuth();
    const { setAccountModalOpen } = useAccountModal();
    if (!authData) return null;
    const openAccountModal = () => {
        setAccountModalOpen(true);
    }
    const doLogout = () => logout();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger className="pressable flex flex-row items-center gap-2 sm:gap-3 rounded-tile outline-none">
                <span className="hidden xl:block font-rad text-caption uppercase tracking-[.1em] text-dim scrim">{authData.name}</span>
                <Avatar
                    className="w-11 h-11 sm:w-10 sm:h-10 lg:w-9 lg:h-9 rounded-tile text-value font-highlight
                        border border-edge transition-colors duration-150 outline-none
                        hover:border-edge-strong"
                    username={authData.name}
                    profilePicture={authData.profilePicture}
                />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
                {/* <DropdownMenuLabel>Your Account</DropdownMenuLabel>
                <DropdownMenuSeparator /> */}

                <DropdownMenuItem className="cursor-pointer" onClick={openAccountModal}>
                    <KeyRoundIcon className="mr-2 h-4 w-4" />
                    Your Account
                </DropdownMenuItem>

                {/* Don't show logout if on NUI */}
                {window.txConsts.isWebInterface && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="cursor-pointer" onClick={doLogout}>
                            <LogOutIcon className="mr-2 h-4 w-4" />
                            Logout
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export function Header() {
    return (
        <header className="sticky top-0 z-20 flex flex-col items-center justify-center
            border-b border-edge bg-glass text-fg backdrop-blur-[2px]">
            <div className="h-14 lg:px-3 px-2 w-full max-w-[1920px] flex flex-row justify-between transition-all">
                <div className="flex flex-row items-center flex-grow gap-5 mr-5">
                    <div className="w-sidebar hidden xl:flex justify-center">
                        <NavLink href="/">
                            <Wordmark />
                        </NavLink>
                    </div>
                    <NavLink href="/" className="hidden sm:max-xl:block">
                        <Wordmark size="sm" />
                    </NavLink>

                    <div className="lg:hidden">
                        <ServerTitle />
                    </div>
                    <nav className="hidden lg:block flex-grow">
                        <DesktopNavbar />
                    </nav>
                </div>

                <div className="flex flex-row items-center gap-2 sm:gap-3">
                    <ButtonToggleServerSheet className="lg:hidden" />
                    <ButtonToggleGlobalMenu className="lg:hidden" />
                    <ButtonTogglePlayerlistSheet className="xl:hidden" />
                    <AuthedHeaderFragment />
                </div>
            </div>
        </header>
    );
}
